<template>
  <div class="shared-filters">
    <align small-gap>
      <search-area
        v-model="localQuery"
        class="search-area-filters"
        :placeholder="placeholder"
        :options="recentSearches"
        :icon="searchIcon"
        @iconClick="$emit('searchIconClick')"
      />

      <div v-show="!hideFilters">
        <icon-button
          icon="tune"
          @click="showFilterModal = true"
        />
      </div>
    </align>

    <modal
      title="Filter"
      size="side"
      :open="showFilterModal"
      @close="closeModal"
    >
      <div class="filters">
        <multiselect
          v-if="showGraders"
          v-model="localFilters.graders"
          label="Graders"
          :options="graderOptions"
        />

        <select-input
          v-if="showHasMatches"
          v-model="localFilters.hasMatches"
          label="Has matches"
          :options="hasMatchesOptions"
        />

        <multiselect
          v-if="showCategories"
          v-model="localFilters.categories"
          label="Categories"
          :options="categoryOptions"
        />

        <div v-if="showValues" class="input-group">
          <currency-input
            v-model="localFilters.minValue"
            label="Min Value"
          />

          <currency-input
            v-model="localFilters.maxValue"
            label="Max Value"
          />
        </div>

        <div v-if="showPrices" class="input-group">
          <currency-input
            v-model="localFilters.minPrice"
            label="Min Price"
          />

          <currency-input
            v-model="localFilters.maxPrice"
            label="Max Price"
          />
        </div>

        <div v-if="showPops" class="input-group">
          <text-input
            v-model.trim="localFilters.minPop"
            type="number"
            label="Min Pop"
          />

          <text-input
            v-model.trim="localFilters.maxPop"
            type="number"
            label="Max Pop"
          />
        </div>

        <div v-if="showCards" class="input-group">
          <text-input
            v-model.trim="localFilters.minCards"
            type="number"
            label="Min Cards"
          />

          <text-input
            v-model.trim="localFilters.maxCards"
            type="number"
            label="Max Cards"
          />
        </div>

        <div v-if="showDates" class="input-group">
          <date-input
            v-model="localFilters.minDate"
            label="Min Date"
            has-clear
            :max="maxDate"
          />

          <date-input
            v-model="localFilters.maxDate"
            label="Max Date"
            has-clear
            :max="maxDate"
          />
        </div>

        <select-input
          v-if="showCollectionStatus"
          v-model="localFilters.sold"
          label="Collection/Sold"
          :options="soldOptions"
        />

        <div v-if="showYears" class="input-group">
          <select-input
            v-model="localFilters.minYear"
            label="Min Year"
            :options="yearOptions"
          />

          <select-input
            v-model="localFilters.maxYear"
            label="Max Year"
            :options="yearOptions"
          />
        </div>

        <multiselect
          v-if="showConditions"
          v-model="localFilters.conditions"
          label="Conditions"
          :options="conditionOptions"
        />

        <multiselect
          v-if="showPlatforms"
          v-model="localFilters.platforms"
          label="Platforms"
          :options="salesPlatforms"
        />

        <select-input
          v-if="showListingType"
          v-model="localFilters.listingType"
          label="Listing Type"
          :options="listingTypeOptions"
        />

        <tag-input
          v-if="showTags"
          v-model="localFilters.tags"
          label="Tags"
        />

        <text-input
          v-if="showSeller"
          v-model.trim="localFilters.seller"
          label="Seller ID"
        />

        <select-input
          v-if="showVerified"
          v-model="localFilters.verified"
          label="Verified"
          :options="verifiedOptions"
        />

        <select-input
          v-if="showCertified"
          v-model="localFilters.certified"
          label="Certified"
          :options="certifiedOptions"
        />

        <select-input
          v-if="showBestOfferStatus"
          v-model="localFilters.bestOfferStatus"
          label="Best Offer Status"
          :options="bestOfferStatusOptions"
        />
      </div>

      <template #footer>
        <align small-gap>
          <btn @click="apply">
            Apply
          </btn>

          <btn kind="secondary" @click="clear">
            Clear
          </btn>
        </align>
      </template>
    </modal>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import clone from 'lodash.clonedeep';
import moment from 'moment';
import debounce from 'lodash.debounce';
import * as Sentry from '@sentry/vue';

import { authService } from '@/services';

import {
  CONDITION_OPTIONS,
  CATEGORY_OPTIONS,
  GRADER_OPTIONS,
  LISTING_TYPE_OPTIONS,
  SOLD_OPTIONS,
  VERIFIED_OPTIONS,
  CERTIFIED_OPTIONS,
  BEST_OFFER_STATUS_OPTIONS,
} from '@cardladder/shared/constants';
import { getYearOptions } from '@cardladder/shared/utils/format.util';

export default {
  props: {
    filters: Object,
    query: String,
    showRecentSearches: Boolean,
    salesPlatforms: Array,
    hideFilters: Boolean,
    searchIcon: String,
    placeholder: {
      type: String,
      default: 'Search',
    },
    properties: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      localFilters: clone(this.filters),
      localQuery: this.query,
      showFilterModal: false,
      maxDate: moment().add(3, 'months').toDate(),
      listingTypeOptions: [{ value: 'all', label: 'All' }, ...LISTING_TYPE_OPTIONS],
      categoryOptions: CATEGORY_OPTIONS,
      graderOptions: GRADER_OPTIONS,
      hasMatchesOptions: [
        { value: 'all', label: 'All' },
        { value: true, label: 'Yes' },
        { value: false, label: 'No' },
      ],
      conditionOptions: CONDITION_OPTIONS,
      soldOptions: SOLD_OPTIONS,
      verifiedOptions: VERIFIED_OPTIONS,
      certifiedOptions: CERTIFIED_OPTIONS,
      bestOfferStatusOptions: BEST_OFFER_STATUS_OPTIONS,
    };
  },

  created() {
    this.saveRecentSearch = debounce(this.saveRecentSearch, 1000);
  },

  watch: {
    filters: {
      handler(value) {
        this.localFilters = clone(value);
      },
      deep: true,
    },

    query(value) {
      this.localQuery = value;
      this.saveRecentSearch();
    },

    localQuery(value) {
      this.$emit('update:query', value);
    },
  },

  computed: {
    ...mapState({
      userId: (state) => state.user.uid,
      recentSearchesObj: (state) => state.user.recentSearches || {},
    }),

    ...mapGetters({
      isStaff: 'isStaff',
    }),

    recentSearches() {
      if (!this.showRecentSearches || this.localQuery) return [];

      return this.recentSearchesObj.global.map((value) => ({
        label: value,
        value,
      }));
    },

    yearOptions() {
      return getYearOptions();
    },

    showGraders() {
      return this.properties?.graders !== undefined;
    },

    showHasMatches() {
      return this.properties?.hasMatches !== undefined;
    },

    showCategories() {
      return this.properties?.categories !== undefined;
    },

    showValues() {
      return this.properties?.minValue !== undefined && this.properties?.maxValue !== undefined;
    },

    showPrices() {
      return this.properties?.minPrice !== undefined && this.properties?.maxPrice !== undefined;
    },

    showPops() {
      return this.properties?.minPop !== undefined && this.properties?.maxPop !== undefined;
    },

    showCards() {
      return this.properties?.minCards !== undefined && this.properties?.maxCards !== undefined;
    },

    showDates() {
      return this.properties?.minDate !== undefined && this.properties?.maxDate !== undefined;
    },

    showConditions() {
      return this.properties?.conditions !== undefined;
    },

    showYears() {
      return this.properties?.minYear !== undefined && this.properties?.maxYear !== undefined;
    },

    showStatus() {
      return this.properties?.status !== undefined && this.isStaff;
    },

    showListingType() {
      return this.properties?.listingType !== undefined;
    },

    showSeller() {
      return this.properties?.seller !== undefined;
    },

    showVerified() {
      return this.properties?.verified !== undefined;
    },

    showCertified() {
      return this.properties?.certified !== undefined;
    },

    showBestOfferStatus() {
      return this.properties?.bestOfferStatus !== undefined;
    },

    showPlatforms() {
      return this.properties?.platforms !== undefined && this.salesPlatforms?.length > 0;
    },

    showTags() {
      return this.properties?.tags !== undefined;
    },

    showCollectionStatus() {
      return this.properties?.sold !== undefined;
    },
  },

  methods: {
    apply() {
      this.$emit('update:filters', clone(this.localFilters));
      this.closeModal();
    },

    async saveRecentSearch() {
      if (!this.showRecentSearches || !this.query.trim()) return;

      const recentSearches = { ...this.recentSearchesObj };
      recentSearches.global = [...new Set([this.query.trim(), ...this.recentSearchesObj.global])].slice(0, 10);

      try {
        await authService.updateUser(this.userId, {
          recentSearches,
        });
      } catch (e) {
        Sentry.captureException(e);
      }
    },

    closeModal() {
      this.localFilters = clone(this.filters);
      this.showFilterModal = false;
    },

    clear() {
      this.$emit('clear');
      this.closeModal();
    },
  },
};
</script>

<style lang="scss" scoped>
.shared-filters {
  @media($medium-down) {
    width: 100%;
  }
}

.search-area-filters {
  flex: 1;
}
</style>
